// Turns an adopted signature into a tight PNG: drawn signatures are cropped
// to the ink, typed ones are rendered in a script font.

import '@fontsource/dancing-script/600.css';

const INK = '#0b1a5a';
const SCRIPT_FONT = '"Dancing Script", "Brush Script MT", cursive';

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

// Crop a drawn signature to its ink plus a little padding.
export async function trimSignature(dataUrl, padding = 8) {
  if (!dataUrl) return null;
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 16) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null; // nothing drawn
  const x = Math.max(0, minX - padding);
  const y = Math.max(0, minY - padding);
  const w = Math.min(width, maxX + padding + 1) - x;
  const h = Math.min(height, maxY + padding + 1) - y;
  // Keep the file small: no wider than 900px.
  const scale = Math.min(1, 900 / w);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(w * scale));
  out.height = Math.max(1, Math.round(h * scale));
  out.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

// Render a typed name in the script font.
export async function renderTypedSignature(text, fontSize = 72) {
  const value = String(text || '').trim();
  if (!value) return null;
  try {
    await document.fonts.load(`600 ${fontSize}px "Dancing Script"`);
  } catch {
    // Falls back to another script font.
  }
  const measure = document.createElement('canvas').getContext('2d');
  measure.font = `600 ${fontSize}px ${SCRIPT_FONT}`;
  const width = Math.ceil(measure.measureText(value).width) + 24;
  const height = Math.ceil(fontSize * 1.45);
  const canvas = document.createElement('canvas');
  canvas.width = Math.min(width, 1400);
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.font = `600 ${fontSize}px ${SCRIPT_FONT}`;
  ctx.fillStyle = INK;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(value, 12, Math.round(fontSize * 1.05), canvas.width - 24);
  return trimSignature(canvas.toDataURL('image/png'), 6);
}

// First letter of each word, skipping words that don't start with a letter
// such as "(test)" or "&".
export const initialsFromName = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .filter((part) => /^\p{L}/u.test(part))
  .map((part) => part[0].toUpperCase())
  .slice(0, 3)
  .join('');

export const SCRIPT_FONT_FAMILY = SCRIPT_FONT;
